import { createReducer } from 'typesafe-actions';
import { ErrorType } from './types';

export type ErrorHandlingState = {
  error?: ErrorType;
  previousErrors: ErrorType[];
};

export type SetErrorPayload = {
  error: ErrorType;
};

export const SET_ERROR = '@errorHandling/SET';

export const showError = (
  from: string,
  innerException: any,
  fatal: boolean,
  message?: string,
  silent?: boolean,
) => {
  const defaultMessage =
    'There has been an error. If the problem persists, please contact technical support.';

  if (!message) {
    // try to retrieve a message from the exception - if there is exactly one property in the object,
    //    then that property is the error message. This is because the API doesn't return the error message
    //    in a consistent format, so we can't (easily) determine what the error is.
    const keys = Object.keys(innerException);
    if (keys.length === 1) {
      message = innerException[keys[0]];
    }
  }

  if (SB_CONFIG.isDebug) {
    if (fatal) {
      console.error(from, innerException, message);
    } else {
      console.warn(from, innerException, message);
    }
  }

  return {
    type: SET_ERROR,
    payload: {
      error: {
        message: message || defaultMessage,
        from,
        innerException,
        fatal,
        silent,
      },
    },
  };
};

export const CLEAR_ERROR = '@errorHandling/CLEAR';

export const clearError = (payload?: SetErrorPayload) => {
  return {
    type: CLEAR_ERROR,
    payload,
  };
};

const initialState: ErrorHandlingState = {
  error: undefined,
  previousErrors: [],
};

const handleSetError = (
  state: ErrorHandlingState,
  action: { payload: SetErrorPayload },
) => {
  // don't allow a fatal error to be replaced
  if (state.error?.fatal) {
    console.error(action.payload?.error);
    return state;
  }

  // silent errors don't show any dialog
  if (action.payload.error.silent) {
    return state;
  }

  return {
    ...state,
    error: action.payload.error,
    previousErrors: [...state.previousErrors, state.error],
  };
};

const handleClearError = (
  state: ErrorHandlingState,
  action: { payload: SetErrorPayload },
) => {
  return {
    ...state,
    error: undefined,
    previousErrors: [...state.previousErrors, state.error],
  };
};

export const reducer = createReducer(initialState)
  .handleAction(SET_ERROR, handleSetError)
  .handleAction(CLEAR_ERROR, handleClearError);
